import { readEnrollmentResponse } from "./enrollmentStatistics";
import { loadEnrollmentReportStatuses, saveEnrollmentReportStatus } from "./enrollmentStatusReport";
import { EnrollmentPaymentReport } from "./EnrollmentPaymentReport";
import React, { useState, useMemo, useRef } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import * as XLSX from "xlsx";
import {
  getAllMatriculas,
  getStudentByIDCensoBatch,
  getResponsavelFinanceiroBatch,
  getFinanceiroByContractsBatch,
  setAllContractsBatch,
} from "../../api/sponteAPI";
import { aulas_inicio_censo_escolar } from "../../utils/YEARCONSTS";
// Removido DatePicker para evitar incompatibilidade de APIs. Usaremos input native date.

// Função auxiliar para verificar se usuário é admin ou coordinator
const isAdminOrCoordinator = () => {
  try {
    const storageData = sessionStorage.getItem("loggedUser");
    if (!storageData) return false;
    const user = JSON.parse(storageData);
    return user?.level === 'admin' || user?.level === 'coordinator';
  } catch (_) {
    return false;
  }
};

// Função auxiliar para verificar se usuário é apenas admin (ações sensíveis como encerrar contratos)
const isAdmin = () => {
  try {
    const storageData = sessionStorage.getItem("loggedUser");
    if (!storageData) return false;
    const user = JSON.parse(storageData);
    return user?.level === 'admin';
  } catch (_) {
    return false;
  }
};

export const CensoEscolar = ({ onNavigateToStats }) => {
  const availableYears = Object.keys(aulas_inicio_censo_escolar.anoLetivo)
    .map((y) => Number(y))
    .sort((a, b) => b - a);
  const defaultYear = availableYears.includes(2027) ? 2027 : availableYears[0];

  const [anoLetivoSelecionado, setAnoLetivoSelecionado] = useState(defaultYear);
  const [situacaoID, setSituacaoID] = useState(6);
  const [matriculas, setMatriculas] = useState([]);
  const [contractStatusesByStudentId, setContractStatusesByStudentId] = useState({});
  const [paymentReportLoading, setPaymentReportLoading] = useState(false);
  const [paymentReportError, setPaymentReportError] = useState("");
  const [paymentReportUpdatedAt, setPaymentReportUpdatedAt] = useState(null);
  const reportRequestRef = useRef(0);
  const enrollmentRequestRef = useRef(0);
  const reportScopeRef = useRef("");
  const reportScope = `${getUnitKey()}:${anoLetivoSelecionado}:${situacaoID}`;
  reportScopeRef.current = reportScope;
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [alunosById, setAlunosById] = useState({}); // { [AlunoID]: alunoCompleto }
  const [pendingIds, setPendingIds] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [totalToFetch, setTotalToFetch] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [forceRefresh, setForceRefresh] = useState(false);

  // Responsável financeiro por aluno
  const [responsavelByAlunoId, setResponsavelByAlunoId] = useState({}); // { [AlunoID]: responsavel }
  const [pendingRespIds, setPendingRespIds] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [totalRespToFetch, setTotalRespToFetch] = useState(0);

  // Financeiro por contrato
  const [financeiroByContratoId, setFinanceiroByContratoId] = useState({}); // { [ContratoID]: financeiro }
  const [pendingFinIds, setPendingFinIds] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [totalFinToFetch, setTotalFinToFetch] = useState(0);
  const [financeiroDerivadoByContratoId, setFinanceiroDerivadoByContratoId] =
    useState({}); // { [ContratoID]: { valorParcela5, descontoPercent } }

  // Estados para controlar quais dados foram buscados
  const [dadosBuscados, setDadosBuscados] = useState({
    matriculas: false,
    alunos: false,
    responsaveis: false,
    financeiro: false
  });

  // Estados específicos de loading para cada tipo de busca
  const [loadingStates, setLoadingStates] = useState({
    matriculas: false,
    alunos: false,
    responsaveis: false,
    financeiro: false
  });

  // Estados de progresso para cada tipo de busca
  const [progressStates, setProgressStates] = useState({
    matriculas: { current: 0, total: 0 },
    alunos: { current: 0, total: 0 },
    responsaveis: { current: 0, total: 0 },
    financeiro: { current: 0, total: 0 },
    atualizarStatus: { current: 0, total: 0 }
  });

  // Estados para modal de pendências
  const [pendenciesModalOpen, setPendenciesModalOpen] = useState(false);
  const [selectedStudentPendencies, setSelectedStudentPendencies] = useState([]);
  const [selectedStudentName, setSelectedStudentName] = useState('');

  // Diálogo de senha para encerrar contratos vigentes
  const [encerrarPasswordDialogOpen, setEncerrarPasswordDialogOpen] = useState(false);
  const [encerrarPasswordInput, setEncerrarPasswordInput] = useState('');
  const [encerrarPasswordError, setEncerrarPasswordError] = useState('');

  const CACHE_TTL_MS = 1000 * 60 * 60 * 12; // 12 horas


  // Função auxiliar para calcular porcentagem de progresso
  const getProgressPercentage = (current, total) => {
    if (total === 0) return 0;
    return Math.round((current / total) * 100);
  };

  // Util: extrai apenas o valor numérico de um texto e formata BRL
  const extractCurrencyFromText = (txt) => {
    if (!txt) return "";
    const str = String(txt);
    const match = str.match(
      /\d{1,3}(?:[.\s]\d{3})*(?:,\d{2})|\d+(?:[.,]\d{2})?/
    );
    if (!match) return "";
    const normalized = match[0]
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const num = Number.parseFloat(normalized);
    if (!Number.isFinite(num)) return "";
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const extractCurrencyNumber = (txt) => {
    if (!txt) return NaN;
    const str = String(txt);
    const match = str.match(
      /\d{1,3}(?:[.\s]\d{3})*(?:,\d{2})|\d+(?:[.,]\d{2})?/
    );
    if (!match) return NaN;
    const normalized = match[0]
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const num = Number.parseFloat(normalized);
    return Number.isFinite(num) ? num : NaN;
  };

  // Util: extrai percentual de um texto (ex.: "Bolsa 30%") e formata "30%"
  const extractPercentFromText = (txt) => {
    if (!txt) return "";
    const str = String(txt);
    const match = str.match(/\d+(?:[.,]\d+)?\s*%?/);
    if (!match) return "";
    const normalized = match[0]
      .replace(/%/g, "")
      .replace(/\s/g, "")
      .replace(",", ".");
    const num = Number.parseFloat(normalized);
    if (!Number.isFinite(num)) return "";
    const fixed = Math.round(num * 100) / 100;
    return `${fixed}%`;
  };

  const extractPercentNumber = (txt) => {
    if (!txt) return NaN;
    const str = String(txt);
    const match = str.match(/\d+(?:[.,]\d+)?\s*%?/);
    if (!match) return NaN;
    const normalized = match[0]
      .replace(/%/g, "")
      .replace(/\s/g, "")
      .replace(",", ".");
    const num = Number.parseFloat(normalized);
    return Number.isFinite(num) ? num : NaN;
  };

  // Busca a 5ª parcela dentro de qualquer estrutura aninhada conhecida
  const computeParcela5FromFinanceiro = (fin) => {
    if (!fin) return { valorParcela5: "", descontoValor: "" };
    let candidate = null;
    const stack = [fin];
    while (stack.length && !candidate) {
      const node = stack.pop();
      if (node && typeof node === "object") {
        const keys = Object.keys(node);
        const hasParcelaNum = keys.some((k) => /parcela|numero/i.test(k));
        if (hasParcelaNum) {
          const numero =
            node.NumeroParcela ||
            node.Parcela ||
            node.Numero ||
            node.NParcela ||
            node.ParcelaNumero;
          if (String(numero) === "5") {
            candidate = node;
            break;
          }
        }
        keys.forEach((k) => {
          const val = node[k];
          if (Array.isArray(val)) val.forEach((v) => stack.push(v));
          else if (val && typeof val === "object") stack.push(val);
        });
      }
    }
    const valorTxt =
      candidate?.ValorParcela ||
      candidate?.Valor ||
      candidate?.ValorOriginal ||
      candidate?.ParcelaValor ||
      "";
    const descontoTxt =
      candidate?.BolsaAssociada ||
      candidate?.Desconto ||
      candidate?.NomeBolsa ||
      candidate?.Bolsa ||
      "";
    return {
      valorParcela5: extractCurrencyFromText(valorTxt),
      valorParcela5Num: extractCurrencyNumber(valorTxt),
      descontoPercent: extractPercentFromText(descontoTxt),
      descontoPercentNum: extractPercentNumber(descontoTxt),
    };
  };

  // Persistência leve do cache entre buscas (por unidade)
  React.useEffect(() => {
    try {
      const unit = getUnitKey();
      const raw = sessionStorage.getItem(`censoAlunoCache:${unit}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.ts && parsed.data) {
          const fresh = Date.now() - parsed.ts < CACHE_TTL_MS;
          if (fresh) setAlunosById(parsed.data);
        } else {
          setAlunosById(parsed || {});
        }
      }
      const rawResp = sessionStorage.getItem(`censoRespCache:v2:${unit}`);
      if (rawResp) {
        const parsedResp = JSON.parse(rawResp);
        if (parsedResp && parsedResp.ts && parsedResp.data) {
          const fresh = Date.now() - parsedResp.ts < CACHE_TTL_MS;
          if (fresh) setResponsavelByAlunoId(parsedResp.data);
        } else {
          setResponsavelByAlunoId(parsedResp || {});
        }
      }
      const rawFin = sessionStorage.getItem(`censoFinCache:v2:${unit}`);
      if (rawFin) {
        const parsedFin = JSON.parse(rawFin);
        if (parsedFin && parsedFin.ts && parsedFin.data) {
          const fresh = Date.now() - parsedFin.ts < CACHE_TTL_MS;
          if (fresh) setFinanceiroByContratoId(parsedFin.data);
        } else {
          setFinanceiroByContratoId(parsedFin || {});
        }
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    try {
      const unit = getUnitKey();
      sessionStorage.setItem(
        `censoAlunoCache:${unit}`,
        JSON.stringify({ ts: Date.now(), data: alunosById })
      );
      sessionStorage.setItem(
        `censoRespCache:v2:${unit}`,
        JSON.stringify({ ts: Date.now(), data: responsavelByAlunoId })
      );
      sessionStorage.setItem(
        `censoFinCache:v2:${unit}`,
        JSON.stringify({ ts: Date.now(), data: financeiroByContratoId })
      );
    } catch (_) {}
  }, [alunosById, responsavelByAlunoId, financeiroByContratoId]);

  // Filtros
  const [filterStartDate, setFilterStartDate] = useState(""); // yyyy-MM-dd
  const [filterEndDate, setFilterEndDate] = useState(""); // yyyy-MM-dd
  const [filterCurso, setFilterCurso] = useState("");
  const [filterTipo, setFilterTipo] = useState(""); // "" | "1" | "2"
  const [filterTurno, setFilterTurno] = useState(""); // "" | "MANHÃ" | "TARDE"
  const [filterExcluirIntegral, setFilterExcluirIntegral] = useState(false); // false = não excluir integral por padrão

  // Ordenação
  const [sortBy, setSortBy] = useState(""); // "data" | "aluno" | "desconto" | "valor" | "pendencias"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const toggleSort = (key) => {
    setSortBy((prevKey) => {
      if (prevKey === key) {
        setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir("asc");
      return key;
    });
  };

  const handleShowAnalise = () => {
    if (onNavigateToStats) {
      // Navegar para a aba de estatísticas com os dados
      onNavigateToStats({
        matriculas,
        alunosById,
        responsavelByAlunoId,
        financeiroByContratoId,
        financeiroDerivadoByContratoId,
        anoLetivo: anoLetivoSelecionado, // Incluir o ano letivo selecionado
        situacaoID,
        queryConfirmed: dadosBuscados.matriculas
      });
    }
  };

  const buildEnrollmentSnapshot = (alunoId) => {
    const matricula = matriculas.find(item => String(getMatriculaAlunoId(item)) === String(alunoId)) || {};
    const aluno = alunosById[String(alunoId)] || {};
    return {
      enrollmentExternalId: matricula.MatriculaID || matricula.MatriculaId || matricula.MatriculaIDContrato,
      contractExternalId: matricula.ContratoID || matricula.ContratoId,
      studentName: matricula.Aluno || matricula.AlunoNome || aluno.Nome,
      classroomName: matricula.Turma || matricula.TurmaNome || aluno.Turma,
      gradeName: matricula.Serie || matricula.SerieAluno || matricula.AnoSerie || aluno.Serie,
      shiftName: matricula.Turno || matricula.TurnoAluno || aluno.Turno,
    };
  };

  const handleRefreshEnrollmentStatuses = async () => {
    const request = ++reportRequestRef.current;
    const scope = reportScope;
    setPaymentReportLoading(true);
    setPaymentReportError("");
    try {
      const statuses = await loadEnrollmentReportStatuses(anoLetivoSelecionado, matriculas);
      if (request !== reportRequestRef.current || scope !== reportScopeRef.current) return;
      setContractStatusesByStudentId(statuses);
      setPaymentReportUpdatedAt(new Date());
    } catch (_) {
      if (request === reportRequestRef.current && scope === reportScopeRef.current) {
        setPaymentReportError("Não foi possível consultar os dados salvos. Os valores anteriores foram mantidos. Tente atualizar novamente; se persistir, solicite a verificação do armazenamento ao suporte.");
      }
    } finally {
      if (request === reportRequestRef.current && scope === reportScopeRef.current) setPaymentReportLoading(false);
    }
  };

  const handleSaveEnrollmentStatus = async (alunoId, changes) => {
    const scope = reportScope;
    const { status, observation } = await saveEnrollmentReportStatus(alunoId, anoLetivoSelecionado, changes, buildEnrollmentSnapshot(alunoId));
    if (scope !== reportScopeRef.current) throw new Error("A consulta mudou durante a gravação. Reabra o relatório para conferir.");
    // Invalida leituras anteriores à gravação para não substituir o registro confirmado.
    reportRequestRef.current += 1;
    setPaymentReportLoading(false);
    setContractStatusesByStudentId(prev => ({ ...prev, [String(alunoId)]: status }));
    setPaymentReportUpdatedAt(new Date());
    if (observation !== undefined) setAlunosById(prev => ({ ...prev, [String(alunoId)]: { ...prev[String(alunoId)], Observacao: observation } }));
    return status;
  };

  const changeEnrollmentContext = (setter, value) => {
    enrollmentRequestRef.current += 1;
    reportRequestRef.current += 1;
    setMatriculas([]);
    setContractStatusesByStudentId({});
    setPaymentReportError("");
    setPaymentReportUpdatedAt(null);
    setPaymentReportLoading(false);
    setLoadingStates(prev => ({ ...prev, matriculas: false }));
    setter(value);
  };

  // Utils de validação básicos
  const sanitizeNumbers = (value) => {
    if (!value) return '';
    // Se for array, pegar primeiro elemento
    const str = Array.isArray(value) ? value[0] : value;
    if (typeof str !== 'string') return String(str || '');
    return str.replace(/\D+/g, '');
  };

  const firstValue = (value) => {
    if (Array.isArray(value)) return firstValue(value[0]);
    if (value && typeof value === "object" && "_" in value) return firstValue(value._);
    return value == null ? "" : String(value);
  };

  const getMatriculaAlunoId = (matricula) => firstValue(
    matricula?.AlunoID ||
    matricula?.AlunoId ||
    matricula?.AlunoIDMatricula ||
    matricula?.AlunoIdSponte
  );

  const statusStudentId = (status) => firstValue(
    status?.studentExternalId ||
    status?.student_external_id ||
    status?.AlunoID ||
    status?.AlunoId ||
    status?.alunoId
  );

  const statusesByStudentId = (statuses = []) =>
    (statuses || []).reduce((acc, status) => {
      const id = statusStudentId(status);
      if (id) acc[String(id)] = status;
      return acc;
    }, {});

  const normalizeString = (value) => {
    if (!value) return '';
    // Se for array, pegar primeiro elemento
    const str = Array.isArray(value) ? value[0] : value;
    if (typeof str !== 'string') return String(str || '');
    return str.trim();
  };

  const isCpfFilled = (cpfMaybe) => {
    const digits = sanitizeNumbers(cpfMaybe);
    return digits.length === 11; // aceitamos como preenchido se houver 11 dígitos
  };

  const isPhoneFilled = (phoneMaybe) => {
    const digits = sanitizeNumbers(phoneMaybe);
    return digits.length >= 10; // celular fixo (10) ou celular (11)
  };

  const isFieldFilled = (value) => {
    if (!value) return false;
    const normalized = normalizeString(value);
    return normalized !== '' && normalized !== 'N/A' && normalized !== 'null' && normalized !== 'undefined';
  };

  // Função para validar se uma matrícula tem dados úteis (não apenas N/A)
  const isValidMatricula = (m) => {
    if (!m || typeof m !== 'object') return false;
    
    // Verificar se tem pelo menos um identificador válido
    const hasId = m.ContratoID || m.ContratoId || m.Contrato || m.ContratoAulaLivreID;
    if (!hasId || hasId === '' || hasId === null || hasId === undefined) return false;
    
    // Verificar se tem pelo menos um campo de dados útil (não apenas N/A)
    // Campos que indicam dados válidos:
    const hasDataMatricula = m.DataMatricula && 
      m.DataMatricula !== 'N/A' && 
      m.DataMatricula !== '' && 
      m.DataMatricula !== null && 
      m.DataMatricula !== undefined;
    
    const hasCurso = (m.NomeCurso && m.NomeCurso !== 'N/A' && m.NomeCurso !== '') ||
      (m.CursoNome && m.CursoNome !== 'N/A' && m.CursoNome !== '');
    
    const hasAluno = (m.Aluno && m.Aluno !== 'N/A' && m.Aluno !== '') ||
      (m.AlunoNome && m.AlunoNome !== 'N/A' && m.AlunoNome !== '');
    
    const hasTurma = (m.NomeTurma && m.NomeTurma !== 'N/A' && m.NomeTurma !== '') ||
      (m.TurmaNome && m.TurmaNome !== 'N/A' && m.TurmaNome !== '');
    
    // Se não tem nenhum dos campos acima, mas tem TipoContratoID válido,
    // ainda assim não é suficiente - precisa ter pelo menos um campo de dados reais
    // (DataMatricula, Curso, Aluno ou Turma) para ser considerado válido
    
    // Pelo menos um desses campos deve ter valor válido
    return hasDataMatricula || hasCurso || hasAluno || hasTurma;
  };

  // Função para verificar pendências de um aluno
  const verificarPendencias = (matricula, aluno, responsavel) => {
    const pendencias = [];
    
    // Normalizar dados do aluno (pode vir como array do XML)
    const alunoCPF = normalizeString(aluno?.CPF || aluno?.Cpf || '');
    const alunoCEP = normalizeString(aluno?.CEP || aluno?.Cep || '');
    const alunoNumero = normalizeString(aluno?.NumeroEndereco || aluno?.Numero || aluno?.NumeroEndereco || '');
    const alunoTelefone = normalizeString(aluno?.Telefone || aluno?.telefone || '');
    const alunoDataNasc = normalizeString(aluno?.DataNascimento || aluno?.DataNasc || aluno?.Nascimento || aluno?.DtNascimento || '');
    const alunoCidadeNatal = normalizeString(aluno?.CidadeNatal || aluno?.Cidade || '');
    const alunoObservacao = normalizeString(aluno?.Observacao || aluno?.Observacoes || '');
    
    // Verificar CPF do aluno
    if (!isCpfFilled(alunoCPF)) {
      pendencias.push('CPF do aluno');
    }
    
    // Verificar endereço (CEP + Número)
    if (!isFieldFilled(alunoCEP) || !isFieldFilled(alunoNumero)) {
      pendencias.push('Endereço completo (CEP + Número)');
    }
    
    // Verificar telefone do aluno
    if (!isPhoneFilled(alunoTelefone)) {
      pendencias.push('Telefone do aluno');
    }
    
    // Verificar data de nascimento do aluno
    if (!isFieldFilled(alunoDataNasc)) {
      pendencias.push('Data de nascimento do aluno');
    }
    
    // Verificar cidade natal
    if (!isFieldFilled(alunoCidadeNatal)) {
      pendencias.push('Cidade natal');
    }
    
    // Verificar CPF do responsável (aceita CPFCNPJ ou variações)
    if (responsavel) {
      const cpfResp = normalizeString(
        responsavel?.CPFCNPJ || 
        responsavel?.CPF || 
        responsavel?.Cpf || 
        responsavel?.cpf || 
        ''
      );
      if (!isCpfFilled(cpfResp)) {
        pendencias.push('CPF do responsável');
      }
      
      // Verificar telefone do responsável (aceita Telefone ou Celular)
      const telResp = normalizeString(
        responsavel?.Telefone || 
        responsavel?.Celular || 
        responsavel?.Celular1 || 
        responsavel?.telefone || 
        responsavel?.celular || 
        ''
      );
      if (!isPhoneFilled(telResp)) {
        pendencias.push('Telefone do responsável');
      }
    } else {
      // Se não há responsável, considerar como pendência
      pendencias.push('CPF do responsável');
      pendencias.push('Telefone do responsável');
    }
    
    // Verificar requerimento preenchido (verificar "ATUALIZADO EM:" nas observações)
    // Aceitar variações: "ATUALIZADO EM:", "Atualizado em:", etc.
    const hasUpdateMarker = alunoObservacao && 
      (alunoObservacao.includes('ATUALIZADO EM:') || 
       alunoObservacao.includes('Atualizado em:') ||
       alunoObservacao.includes('atualizado em:'));
    
    if (!hasUpdateMarker) {
      pendencias.push('Requerimento preenchido');
    }
    
    return pendencias;
  };

  // Função para abrir modal de pendências
  const handleOpenPendencies = (matricula, aluno, responsavel) => {
    const pendencias = verificarPendencias(matricula, aluno, responsavel);
    setSelectedStudentPendencies(pendencias);
    setSelectedStudentName(aluno?.Nome || 'Aluno não encontrado');
    setPendenciesModalOpen(true);
  };


  // Opções de situação
  const situacoes = [
    { id: 1, label: "Vigente" },
    { id: 2, label: "Encerrado" },
    { id: 3, label: "Rescindido" },
    { id: 4, label: "Cancelado" },
    { id: 5, label: "Trancado" },
    { id: 6, label: "Pré-Matrícula" },
  ];

  // Converte DD/MM/YYYY -> YYYY/MM/DD
  const brToApiDate = (dateBr) => {
    const [day, month, year] = String(dateBr).split("/");
    return `${year}/${month}/${day}`;
  };

  // Obtém unidade logada e retorna chave 'campina' | 'patos'
  function getUnitKey() {
    try {
      const storageData = sessionStorage.getItem("loggedUser");
      if (!storageData) return "campina";
      const user = JSON.parse(storageData);
      return user.unit === "CAMPINA GRANDE" ? "campina" : "patos";
    } catch (_) {
      return "campina";
    }
  };

  // Mapeia TipoContratoID para descrição amigável
  const mapTipoMatricula = (tipoId) => {
    const value = Number(tipoId);
    if (value === -1) return "Matrícula";
    return "Rematrícula";
  };

  // Extrai o turno do NomeTurma (texto entre parênteses)
  const extractTurno = (nomeTurma) => {
    const turmaNormalizada = normalizeString(nomeTurma);
    const match = /\(([^)]+)\)/.exec(turmaNormalizada);
    return match ? match[1] : "-";
  };

  // Mantido utilitário para possível uso futuro de comparação direta/visão.

  // Converte dd/MM/yyyy -> yyyy-MM-dd
  const brToIso = (ddmmyyyy) => {
    if (!ddmmyyyy) return "";
    const [day, month, year] = String(ddmmyyyy).split("/");
    return `${year}-${month}-${day}`;
  };

  const clearFilters = () => {
    setFilterStartDate("");
    setFilterEndDate("");
    setFilterCurso("");
    setFilterTipo("");
    setFilterTurno("");
    setFilterExcluirIntegral(false);
  };

  const handleStartDateChange = (value) => {
    setFilterStartDate(value);
    // Sempre inicializa/atualiza o fim com o mesmo valor do início
    // O usuário pode alterar manualmente depois, se quiser um intervalo
    setFilterEndDate(value);
  };

  const filteredMatriculas = useMemo(() => {
    const list = Array.isArray(matriculas) ? matriculas : [];
    // Primeiro, filtrar objetos inválidos ou sem dados úteis
    const validList = list.filter(isValidMatricula);
    
    // Depois, aplicar os filtros do usuário
    return validList.filter((m) => {
      // Filtro de integral (sempre aplicado primeiro)
      if (filterExcluirIntegral) {
        const turma = normalizeString(m?.NomeTurma || m?.TurmaNome || "");
        if (turma.toLowerCase().includes("integral")) return false;
      }
      
      // Data da matrícula (intervalo inclusivo)
      if (filterStartDate || filterEndDate) {
        const iso = brToIso(m?.DataMatricula || ""); // yyyy-MM-dd
        if (filterStartDate && iso < filterStartDate) return false;
        if (filterEndDate && iso > filterEndDate) return false;
      }
      // Curso (contains, case-insensitive)
      if (filterCurso) {
        const curso = normalizeString(m?.NomeCurso || m?.CursoNome || "");
        if (!curso.toLowerCase().includes(filterCurso.toLowerCase())) return false;
      }
      // Tipo (1 Matrícula, 2 Rematrícula)
      if (filterTipo) {
        if (String(m?.TipoContratoID) !== String(filterTipo)) return false;
      }
      // Turno (extraído do nome da turma)
      if (filterTurno) {
        const turno = (
          extractTurno(m?.NomeTurma || m?.TurmaNome) || ""
        ).toUpperCase();
        if (turno !== filterTurno.toUpperCase()) return false;
      }
      return true;
    });
  }, [
    matriculas,
    filterStartDate,
    filterEndDate,
    filterCurso,
    filterTipo,
    filterTurno,
    filterExcluirIntegral,
  ]);

  const sortedMatriculas = useMemo(() => {
    const list = [...filteredMatriculas];
    
    if (!sortBy) return list;
    
    // Ordenar a lista
    list.sort((a, b) => {
      if (sortBy === "data") {
        const ia = brToIso(a?.DataMatricula || "");
        const ib = brToIso(b?.DataMatricula || "");
        return ia.localeCompare(ib);
      }
      
      if (sortBy === "aluno") {
        const na = normalizeString(a?.Aluno || a?.AlunoNome || "").toLowerCase();
        const nb = normalizeString(b?.Aluno || b?.AlunoNome || "").toLowerCase();
        return na.localeCompare(nb);
      }
      
      if (sortBy === "valor" || sortBy === "desconto") {
        // Buscar dados financeiros diretamente
        const contratoA = a?.ContratoID || a?.ContratoId || a?.Contrato || a?.ContratoAulaLivreID;
        const contratoB = b?.ContratoID || b?.ContratoId || b?.Contrato || b?.ContratoAulaLivreID;
        
        const fa = financeiroDerivadoByContratoId[String(contratoA)] || {};
        const fb = financeiroDerivadoByContratoId[String(contratoB)] || {};
        
        let valueA, valueB;
        
        if (sortBy === "valor") {
          valueA = fa.valorParcela5;
          valueB = fb.valorParcela5;
        } else if (sortBy === "desconto") {
          valueA = fa.descontoPercent;
          valueB = fb.descontoPercent;
        }
        
        
        // Verificar se os valores são válidos
        const aIsValid = Number.isFinite(valueA) && valueA !== null && valueA !== undefined;
        const bIsValid = Number.isFinite(valueB) && valueB !== null && valueB !== undefined;
        
        // Se ambos são válidos, comparar numericamente
        if (aIsValid && bIsValid) {
          return valueA - valueB;
        }
        
        // Se apenas A é válido, A vem primeiro
        if (aIsValid && !bIsValid) {
          return -1;
        }
        
        // Se apenas B é válido, B vem primeiro
        if (!aIsValid && bIsValid) {
          return 1;
        }
        
        // Se nenhum é válido, manter ordem original
        return 0;
      }
      
      if (sortBy === "pendencias") {
        // Buscar dados do aluno e responsável para verificar pendências
        const alunoIdA = getMatriculaAlunoId(a);
        const alunoIdB = getMatriculaAlunoId(b);
        
        const alunoA = alunosById[String(alunoIdA)] || {};
        const alunoB = alunosById[String(alunoIdB)] || {};
        const respA = responsavelByAlunoId[String(alunoIdA)] || {};
        const respB = responsavelByAlunoId[String(alunoIdB)] || {};
        
        // Verificar pendências para cada aluno
        const pendenciasA = verificarPendencias(a, alunoA, respA);
        const pendenciasB = verificarPendencias(b, alunoB, respB);
        
        // Comparar quantidade de pendências (0 = sem pendências, >0 = com pendências)
        const temPendenciasA = pendenciasA.length > 0;
        const temPendenciasB = pendenciasB.length > 0;
        
        // Se ambos têm ou não têm pendências, comparar por quantidade
        if (temPendenciasA === temPendenciasB) {
          return pendenciasA.length - pendenciasB.length;
        }
        
        // Se apenas A tem pendências, A vem primeiro (ou último dependendo da direção)
        if (temPendenciasA && !temPendenciasB) {
          return -1;
        }
        
        // Se apenas B tem pendências, B vem primeiro (ou último dependendo da direção)
        if (!temPendenciasA && temPendenciasB) {
          return 1;
        }
        
        return 0;
      }
      
      return 0;
    });
    
    // Aplicar direção da ordenação
    if (sortDir === "desc") {
      list.reverse();
    }
    
    return list;
  }, [filteredMatriculas, sortBy, sortDir, financeiroDerivadoByContratoId]);

  const getClassroomName = (matricula) =>
    normalizeString(
      matricula?.NomeTurma ||
      matricula?.TurmaNome ||
      matricula?.Turma ||
      matricula?.NomeTurmaContrato ||
      "Turma não informada"
    ) || "Turma não informada";

  const getStudentName = (matricula) =>
    normalizeString(matricula?.Aluno || matricula?.AlunoNome || matricula?.NomeAluno || "Nome não informado") || "Nome não informado";

  const groupedByClassroom = useMemo(() => {
    const groups = {};
    sortedMatriculas.filter(isValidMatricula).forEach((matricula) => {
      const classroom = getClassroomName(matricula);
      const type = mapTipoMatricula(matricula.TipoContratoID);
      if (!groups[classroom]) {
        groups[classroom] = { classroom, total: 0, matricula: 0, rematricula: 0, rows: [] };
      }
      groups[classroom].total += 1;
      if (type === "Matrícula") groups[classroom].matricula += 1;
      else groups[classroom].rematricula += 1;
      groups[classroom].rows.push({
        name: getStudentName(matricula),
        type,
      });
    });

    return Object.values(groups)
      .map((group) => ({
        ...group,
        rows: group.rows.sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
      }))
      .sort((a, b) => a.classroom.localeCompare(b.classroom, "pt-BR"));
  }, [sortedMatriculas]);

  const printGroupedByClassroom = () => {
    if (!groupedByClassroom.length) return;
    const escapeHtml = (value) => String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    const total = groupedByClassroom.reduce((sum, group) => sum + group.total, 0);
    const totalMatricula = groupedByClassroom.reduce((sum, group) => sum + group.matricula, 0);
    const totalRematricula = groupedByClassroom.reduce((sum, group) => sum + group.rematricula, 0);
    const generatedAt = new Date().toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Relatório de Matrículas por Turma</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; background: #fff; }
            .page { padding: 12mm 10mm 10mm; }
            .header { border-bottom: 2px solid #0f766e; padding-bottom: 7px; margin-bottom: 8px; display: flex; justify-content: space-between; gap: 16px; align-items: flex-end; }
            .brand { color: #0f766e; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
            h1 { font-size: 19px; margin: 2px 0 0; line-height: 1.1; }
            .meta { color: #4b5563; font-size: 10px; text-align: right; line-height: 1.3; }
            .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin: 8px 0 10px; }
            .metric { border: 1px solid #d1d5db; border-radius: 5px; padding: 5px 8px; background: #f9fafb; }
            .metric-label { color: #6b7280; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
            .metric-value { display: block; font-size: 16px; font-weight: 700; margin-top: 1px; color: #111827; }
            .group { page-break-inside: avoid; border: 1px solid #d1d5db; border-radius: 5px; margin: 0 0 7px; overflow: hidden; }
            .group-header { background: #0f766e; color: white; padding: 5px 8px; display: flex; justify-content: space-between; gap: 10px; align-items: center; }
            .group-title { font-size: 12px; font-weight: 700; }
            .group-totals { font-size: 10px; white-space: nowrap; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border-bottom: 1px solid #e5e7eb; padding: 3px 7px; font-size: 10.5px; text-align: left; vertical-align: top; line-height: 1.2; }
            th { background: #f3f4f6; color: #374151; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
            tr:last-child td { border-bottom: 0; }
            .index { width: 32px; color: #6b7280; text-align: right; }
            .type { width: 105px; }
            .badge { display: inline-block; border-radius: 999px; padding: 1px 6px; font-size: 9px; font-weight: 700; background: #e0f2fe; color: #075985; }
            .badge.rematricula { background: #ecfdf5; color: #047857; }
            @page { size: A4; margin: 0; }
            @media print {
              .page { padding: 10mm 9mm; }
              .group { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="page">
          <div class="header">
            <div>
              <div class="brand">Colégio Autêntico</div>
              <h1>Relatório de Matrículas por Turma</h1>
            </div>
            <div class="meta">
              Ano letivo ${escapeHtml(anoLetivoSelecionado)}<br />
              ${escapeHtml(situacoes.find((s) => String(s.id) === String(situacaoID))?.label || "")}<br />
              Gerado em ${escapeHtml(generatedAt)}
            </div>
          </div>
          <div class="summary">
            <div class="metric"><span class="metric-label">Turmas</span><span class="metric-value">${groupedByClassroom.length}</span></div>
            <div class="metric"><span class="metric-label">Total de alunos</span><span class="metric-value">${total}</span></div>
            <div class="metric"><span class="metric-label">Matrículas</span><span class="metric-value">${totalMatricula}</span></div>
            <div class="metric"><span class="metric-label">Rematrículas</span><span class="metric-value">${totalRematricula}</span></div>
          </div>
          ${groupedByClassroom.map((group) => `
            <div class="group">
              <div class="group-header">
                <div class="group-title">${escapeHtml(group.classroom)}</div>
                <div class="group-totals">Total ${group.total} | Matrículas ${group.matricula} | Rematrículas ${group.rematricula}</div>
              </div>
              <table>
                <thead><tr><th class="index">#</th><th>Aluno</th><th class="type">Tipo</th></tr></thead>
                <tbody>
                  ${group.rows.map((row, index) => `<tr><td class="index">${index + 1}</td><td>${escapeHtml(row.name)}</td><td class="type"><span class="badge ${row.type === "Rematrícula" ? "rematricula" : ""}">${escapeHtml(row.type)}</span></td></tr>`).join("")}
                </tbody>
              </table>
            </div>
          `).join("")}
          </div>
          <script>
            window.addEventListener("load", function () {
              window.setTimeout(function () {
                window.focus();
                window.print();
              }, 250);
            });
          </script>
        </body>
      </html>
    `;
    const printUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    const printWindow = window.open(printUrl, "_blank");
    if (!printWindow) {
      URL.revokeObjectURL(printUrl);
      return;
    }
    window.setTimeout(() => URL.revokeObjectURL(printUrl), 60000);
  };

  // Função para exportar dados para Excel
  const handleExportToExcel = () => {
    if (sortedMatriculas.length === 0) {
      return;
    }

    // Preparar cabeçalhos baseados nas colunas disponíveis
    const headers = ["Data da Matrícula", "Curso", "Tipo da Matrícula", "Turno"];
    
    if (dadosBuscados.alunos) {
      headers.push(
        "Nome do Aluno",
        "Data de Nascimento",
        "CPF",
        "CEP",
        "Nº Endereço",
        "Nº Matrícula"
      );
    }
    
    if (dadosBuscados.responsaveis) {
      headers.push("Resp. Financeiro", "CPF Resp.");
    }
    
    if (dadosBuscados.financeiro) {
      headers.push("Valor da Parcela", "Desconto");
    }
    
    headers.push("Pendências");

    // Preparar dados formatados
    const excelData = sortedMatriculas.map((matricula) => {
      const alunoId = getMatriculaAlunoId(matricula);
      const aluno = alunosById[String(alunoId)] || {};
      const resp = responsavelByAlunoId[String(alunoId)] || {};
      const contratoId =
        matricula?.ContratoID ||
        matricula?.ContratoId ||
        matricula?.Contrato ||
        matricula?.ContratoAulaLivreID;
      const finDerivado =
        financeiroDerivadoByContratoId[String(contratoId)] || {};

      const row = [
        matricula.DataMatricula || "N/A",
        matricula.NomeCurso || matricula.CursoNome || "N/A",
        mapTipoMatricula(matricula.TipoContratoID),
        extractTurno(matricula.NomeTurma || matricula.TurmaNome),
      ];

      // Dados do aluno
      if (dadosBuscados.alunos) {
        row.push(
          matricula.Aluno || matricula.AlunoNome || "N/A",
          aluno.DataNascimento ||
            aluno.DataNasc ||
            aluno.Nascimento ||
            aluno.DtNascimento ||
            matricula.DataNascimento ||
            "N/A",
          aluno.CPF ||
            aluno.Cpf ||
            aluno.CPFAluno ||
            matricula.CPF ||
            matricula.Cpf ||
            "N/A",
          aluno.CEP || aluno.Cep || "N/A",
          aluno.NumeroEndereco || aluno.Numero || "N/A",
          aluno.NumeroMatricula || matricula.NumeroMatricula || "N/A"
        );
      }

      // Dados do responsável
      if (dadosBuscados.responsaveis) {
        row.push(
          resp.Nome || resp.nome || resp.ResponsavelNome || "N/A",
          resp.CPFCNPJ || resp.CPF || resp.Cpf || "N/A"
        );
      }

      // Dados financeiros (formatados para Excel)
      if (dadosBuscados.financeiro) {
        // Valor da Parcela: número puro (sem formatação de moeda)
        // O Excel aplicará formatação automática conforme configuração regional
        const valorNum = finDerivado.valorParcela5;
        const valorFormatado =
          typeof valorNum === "number" && !isNaN(valorNum)
            ? valorNum
            : "N/A";

        // Desconto: número puro (permite cálculos no Excel)
        // O Excel em pt-BR exibirá automaticamente com vírgula como separador decimal
        const descontoNum = finDerivado.descontoPercent;
        const descontoFormatado =
          typeof descontoNum === "number" && !isNaN(descontoNum)
            ? descontoNum
            : "N/A";

        row.push(valorFormatado, descontoFormatado);
      }

      // Pendências
      const pendencias = verificarPendencias(matricula, aluno, resp);
      const temPendencias = pendencias.length > 0;
      row.push(temPendencias ? "Sim" : "Não");

      return row;
    });

    // Criar workbook
    const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    
    // Ajustar largura das colunas
    const colWidths = headers.map((header, idx) => {
      const maxLength = Math.max(
        header.length,
        ...excelData.map((row) => String(row[idx] || "").length)
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    ws["!cols"] = colWidths;

    // Criar workbook e adicionar planilha
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Matrículas");

    // Gerar nome do arquivo com data e hora
    const now = new Date();
    const dateStr = now
      .toLocaleDateString("pt-BR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\//g, "-");
    const timeStr = now
      .toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(/:/g, "-");
    const fileName = `Matrículas_${dateStr}_${timeStr}.xlsx`;

    // Fazer download
    XLSX.writeFile(wb, fileName);
  };

  // Função para buscar dados dos alunos
  const handleBuscarAlunos = async (refresh = false, selectedIds = null) => {
    const refreshStudents = refresh === true || forceRefresh;
    const scope = reportScope;
    if (selectedIds) setPaymentReportError("");
    if (matriculas.length === 0) return;
    
    try {
      setLoadingStates(prev => ({ ...prev, alunos: true }));
      
      const allIds = selectedIds || Array.from(
        new Set(
          matriculas
            .map(
              (m) =>
                getMatriculaAlunoId(m)
            )
            .filter(Boolean)
        )
      );
      
      const knownIds = new Set(Object.keys(alunosById));
      const idsToFetch = refreshStudents
        ? allIds
        : allIds.filter((id) => !knownIds.has(String(id)) || !alunosById[String(id)]);
        
      if (idsToFetch.length) {
        setPendingIds(new Set(idsToFetch.map(String)));
        setTotalToFetch(idsToFetch.length);
        setProgressStates(prev => ({ 
          ...prev, 
          alunos: { current: 0, total: idsToFetch.length } 
        }));
      } else {
        setTotalToFetch(0);
        setProgressStates(prev => ({ 
          ...prev, 
          alunos: { current: 0, total: 0 } 
        }));
      }
      
      // Usar endpoint batch para reduzir número de requisições
      // Processar em chunks de 50 (máximo permitido pelo backend)
      const chunkSize = 50;
      const newEntries = {};
      
      for (let i = 0; i < idsToFetch.length; i += chunkSize) {
        const chunk = idsToFetch.slice(i, i + chunkSize);
        
        try {
          // Chamar endpoint batch que gerencia rate limiting automaticamente
          const batchResponse = await getStudentByIDCensoBatch(
            chunk,
            refreshStudents
          );
          
          // O endpoint retorna { data: {...}, stats: {...} }
          const batchResults = batchResponse?.data || batchResponse || {};
          
          // Processar resultados
          Object.entries(batchResults).forEach(([alunoId, aluno]) => {
            newEntries[alunoId] = aluno || null;
            setPendingIds((prev) => {
              const next = new Set(Array.from(prev));
              next.delete(String(alunoId));
              return next;
            });
            
            // Atualizar progresso
            setProgressStates(prev => {
              const current = prev.alunos.current + 1;
              return {
                ...prev,
                alunos: { ...prev.alunos, current }
              };
            });
          });
          
          // Se não processou nenhum resultado, marcar todos como processados
          if (Object.keys(batchResults).length === 0) {
            chunk.forEach(id => {
              newEntries[id] = null;
              setPendingIds((prev) => {
                const next = new Set(Array.from(prev));
                next.delete(String(id));
                return next;
              });
              setProgressStates(prev => {
                const current = prev.alunos.current + 1;
                return {
                  ...prev,
                  alunos: { ...prev.alunos, current }
                };
              });
            });
          }
        } catch (error) {
          // Se for erro de rate limit, mostrar alerta e parar
          if (error.response?.status === 429 || error.rateLimitExceeded) {
            const waitTime = error.response?.data?.waitTime || error.waitTime || 60;
            const message = error.response?.data?.error || error.message || 'Limite de requisições atingido';
            alert(`⚠️ ${message}\n\nAguarde ${waitTime} segundos antes de tentar novamente.`);
            break; // Parar processamento
          }
          
          console.error(`Erro ao buscar chunk de alunos:`, error);
          // Continuar com próximo chunk mesmo se houver erro (exceto rate limit)
          chunk.forEach(id => {
            newEntries[id] = null;
            setPendingIds((prev) => {
              const next = new Set(Array.from(prev));
              next.delete(String(id));
              return next;
            });
            setProgressStates(prev => {
              const current = prev.alunos.current + 1;
              return {
                ...prev,
                alunos: { ...prev.alunos, current }
              };
            });
          });
        }
      }
      
      if (scope !== reportScopeRef.current) return;
      if (selectedIds && idsToFetch.some(id => !newEntries[id])) {
        setPaymentReportError("Algumas observações antigas não puderam ser consultadas. Os dados anteriores foram preservados; tente consultar novamente antes de exportar.");
      }
      const successfulEntries = Object.fromEntries(Object.entries(newEntries).filter(([, value]) => Boolean(value)));
      if (Object.keys(successfulEntries).length) {
        setAlunosById(prev => ({ ...prev, ...successfulEntries }));
      }
      if (!idsToFetch.length) setPendingIds(new Set());
      
      // Marcar que os dados dos alunos foram buscados
      setDadosBuscados(prev => ({ ...prev, alunos: true }));
      
    } catch (error) {
      console.error("Erro ao buscar dados dos alunos:", error);
    } finally {
      setLoadingStates(prev => ({ ...prev, alunos: false }));
    }
  };

  // Função para buscar dados dos responsáveis
  const handleBuscarResponsaveis = async () => {
    if (matriculas.length === 0) return;
    
    try {
      setLoadingStates(prev => ({ ...prev, responsaveis: true }));
      
      const allIds = Array.from(
        new Set(
          matriculas
            .map(
              (m) =>
                getMatriculaAlunoId(m)
            )
            .filter(Boolean)
        )
      );
      
      const knownResp = new Set(Object.keys(responsavelByAlunoId));
      const respIdsToFetch = forceRefresh
        ? allIds
        : allIds.filter((id) => !knownResp.has(String(id)));
        
      if (respIdsToFetch.length) {
        setPendingRespIds(new Set(respIdsToFetch.map(String)));
        setTotalRespToFetch(respIdsToFetch.length);
        setProgressStates(prev => ({ 
          ...prev, 
          responsaveis: { current: 0, total: respIdsToFetch.length } 
        }));
      } else {
        setTotalRespToFetch(0);
        setProgressStates(prev => ({ 
          ...prev, 
          responsaveis: { current: 0, total: 0 } 
        }));
      }
      
      // Usar endpoint batch para reduzir número de requisições
      // Processar em chunks de 50 (máximo permitido pelo backend)
      const chunkSize = 50;
      const newRespEntries = {};
      
      for (let i = 0; i < respIdsToFetch.length; i += chunkSize) {
        const chunk = respIdsToFetch.slice(i, i + chunkSize);
        
        try {
          // Chamar endpoint batch que gerencia rate limiting automaticamente
          const batchResponse = await getResponsavelFinanceiroBatch(
            chunk,
            forceRefresh
          );
          
          // O endpoint retorna { data: {...}, stats: {...} }
          const batchResults = batchResponse?.data || batchResponse || {};
          const batchErrors = batchResponse?.errors || {};
          
          // Processar resultados
          Object.entries(batchResults).forEach(([alunoId, resp]) => {
            const resolved = resp || null;
            newRespEntries[alunoId] = resolved;
          });

          if (Object.keys(batchErrors).length > 0) {
            console.warn("Responsáveis não carregados; poderão ser tentados novamente:", batchErrors);
          }

          setPendingRespIds((prev) => {
            const next = new Set(prev);
            chunk.forEach((id) => next.delete(String(id)));
            return next;
          });
          setProgressStates(prev => ({
            ...prev,
            responsaveis: {
              ...prev.responsaveis,
              current: Math.min(
                prev.responsaveis.current + chunk.length,
                prev.responsaveis.total
              )
            }
          }));
        } catch (error) {
          // Se for erro de rate limit, mostrar alerta e parar
          if (error.response?.status === 429 || error.rateLimitExceeded) {
            const waitTime = error.response?.data?.waitTime || error.waitTime || 60;
            const message = error.response?.data?.error || error.message || 'Limite de requisições atingido';
            alert(`⚠️ ${message}\n\nAguarde ${waitTime} segundos antes de tentar novamente.`);
            break; // Parar processamento
          }
          
          console.error(`Erro ao buscar chunk de responsáveis:`, error);
          // Falhas não entram no cache como null; um novo clique tentará novamente.
          setPendingRespIds((prev) => {
            const next = new Set(prev);
            chunk.forEach((id) => next.delete(String(id)));
            return next;
          });
          setProgressStates(prev => ({
            ...prev,
            responsaveis: {
              ...prev.responsaveis,
              current: Math.min(
                prev.responsaveis.current + chunk.length,
                prev.responsaveis.total
              )
            }
          }));
        }
      }
      
      if (Object.keys(newRespEntries).length) {
        setResponsavelByAlunoId((prev) => ({ ...prev, ...newRespEntries }));
      }
      if (!respIdsToFetch.length) setPendingRespIds(new Set());
      
      // Marcar que os dados dos responsáveis foram buscados
      setDadosBuscados(prev => ({ ...prev, responsaveis: true }));
      
    } catch (error) {
      console.error("Erro ao buscar dados dos responsáveis:", error);
    } finally {
      setLoadingStates(prev => ({ ...prev, responsaveis: false }));
    }
  };

  // Função para buscar dados financeiros
  const handleBuscarFinanceiro = async () => {
    if (matriculas.length === 0) return;
    
    try {
      setLoadingStates(prev => ({ ...prev, financeiro: true }));
      
      const allContratoIds = Array.from(
        new Set(
          matriculas
            .map(
              (m) =>
                m?.ContratoID ||
                m?.ContratoId ||
                m?.Contrato ||
                m?.ContratoAulaLivreID
            )
            .filter(Boolean)
        )
      );
      
      const knownFin = new Set(Object.keys(financeiroByContratoId));
      const finIdsToFetch = forceRefresh
        ? allContratoIds
        : allContratoIds.filter((id) => !knownFin.has(String(id)));
        
      if (finIdsToFetch.length) {
        setPendingFinIds(new Set(finIdsToFetch.map(String)));
        setTotalFinToFetch(finIdsToFetch.length);
        setProgressStates(prev => ({ 
          ...prev, 
          financeiro: { current: 0, total: finIdsToFetch.length } 
        }));
      } else {
        setTotalFinToFetch(0);
        setProgressStates(prev => ({ 
          ...prev, 
          financeiro: { current: 0, total: 0 } 
        }));
      }
      
      // Usar endpoint batch com rate limiting
      // Processar em chunks de 100 (máximo permitido) para reduzir número de requisições
      const chunkSize = 100;
      const newFinEntries = {};
      
      // Processar chunks em paralelo (até 3 chunks simultâneos) para ser mais rápido
      const chunks = [];
      for (let i = 0; i < finIdsToFetch.length; i += chunkSize) {
        chunks.push(finIdsToFetch.slice(i, i + chunkSize));
      }
      
      // Processar até 3 chunks em paralelo
      const maxParallelChunks = 3;
      let shouldStop = false; // Flag para parar processamento quando atingir rate limit
      
      for (let i = 0; i < chunks.length && !shouldStop; i += maxParallelChunks) {
        const parallelChunks = chunks.slice(i, i + maxParallelChunks);
        
        // Capturar o valor atual de shouldStop para evitar referência insegura
        const currentShouldStop = shouldStop;
        
        const results = await Promise.allSettled(
          parallelChunks.map(async (chunk) => {
            // Verificar se deve parar antes de processar
            if (currentShouldStop) {
              return;
            }
            
            try {
              // Chamar endpoint batch que gerencia rate limiting automaticamente
              const batchResponse = await getFinanceiroByContractsBatch(
                chunk,
                forceRefresh
              );
              
              // O endpoint retorna { data: {...}, stats: {...} }
              const batchResults = batchResponse?.data || batchResponse || {};
              const batchErrors = batchResponse?.errors || {};
              
              // Processar resultados
              Object.entries(batchResults).forEach(([contratoId, fin]) => {
                // Manter todas as parcelas. A parcela 5 pode não ser o primeiro item.
                newFinEntries[contratoId] = fin ?? null;
              });

              if (Object.keys(batchErrors).length > 0) {
                console.warn("Contratos financeiros não carregados; poderão ser tentados novamente:", batchErrors);
              }

              setPendingFinIds((prev) => {
                const next = new Set(prev);
                chunk.forEach((id) => next.delete(String(id)));
                return next;
              });
              setProgressStates(prev => ({
                ...prev,
                financeiro: {
                  ...prev.financeiro,
                  current: Math.min(
                    prev.financeiro.current + chunk.length,
                    prev.financeiro.total
                  )
                }
              }));
              
              return { success: true };
            } catch (error) {
              // Se for erro de rate limit, mostrar alerta e parar
              if (error.response?.status === 429 || error.rateLimitExceeded) {
                const waitTime = error.response?.data?.waitTime || error.waitTime || 60;
                const message = error.response?.data?.error || error.message || 'Limite de requisições atingido';
                alert(`⚠️ ${message}\n\nAguarde ${waitTime} segundos antes de tentar novamente.`);
                // Não modificar shouldStop aqui - será feito após Promise.allSettled
                throw error; // Re-throw para ser capturado pelo Promise.allSettled
              }
              
              console.error(`Erro ao buscar chunk de contratos:`, error);
              // Não persistir falha como null: permite nova tentativa no próximo clique.
              setPendingFinIds((prev) => {
                const next = new Set(prev);
                chunk.forEach((id) => next.delete(String(id)));
                return next;
              });
              setProgressStates(prev => ({
                ...prev,
                financeiro: {
                  ...prev.financeiro,
                  current: Math.min(
                    prev.financeiro.current + chunk.length,
                    prev.financeiro.total
                  )
                }
              }));
              
              return { success: false, error };
            }
          })
        );
        
        // Verificar se algum resultado foi rejeitado por rate limit
        const hasRateLimitError = results.some(result => 
          result.status === 'rejected' && 
          (result.reason?.response?.status === 429 || result.reason?.rateLimitExceeded)
        );
        
        if (hasRateLimitError) {
          shouldStop = true;
        }
      }
      
      if (Object.keys(newFinEntries).length) {
        setFinanceiroByContratoId((prev) => ({ ...prev, ...newFinEntries }));
      }
      if (!finIdsToFetch.length) setPendingFinIds(new Set());

      // Derivar dados financeiros (valorParcela5 e descontoPercent)
      const derivados = {};
      Object.entries({ ...financeiroByContratoId, ...newFinEntries }).forEach(
        ([contratoId, fin]) => {
          if (!fin) return;
          // Usar a função existente que já funcionava
          const derivado = computeParcela5FromFinanceiro(fin);
          
          derivados[contratoId] = {
            valorParcela5: derivado.valorParcela5Num, // Usar o número, não a string formatada
            descontoPercent: derivado.descontoPercentNum // Usar o número, não a string formatada
          };
        }
      );
      setFinanceiroDerivadoByContratoId(derivados);
      
      // Marcar que os dados financeiros foram buscados
      setDadosBuscados(prev => ({ ...prev, financeiro: true }));
      
    } catch (error) {
      console.error("Erro ao buscar dados financeiros:", error);
      // Se for erro de rate limit, mostrar alerta e parar
      if (error.response?.status === 429 || error.rateLimitExceeded) {
        const waitTime = error.response?.data?.waitTime || error.waitTime || 60;
        const message = error.response?.data?.error || error.message || 'Limite de requisições atingido';
        alert(`⚠️ ${message}\n\nAguarde ${waitTime} segundos antes de tentar novamente.`);
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, financeiro: false }));
    }
  };

  const handleGetMatriculas = async () => {
    const request = ++enrollmentRequestRef.current;
    const scope = reportScope;
    let timeoutId;
    try {
      setLoadingStates(prev => ({ ...prev, matriculas: true }));
      
      // Determinar data de início a partir do ano letivo e unidade
      const unidadeKey = getUnitKey();
      const dataInicioBr =
        aulas_inicio_censo_escolar.anoLetivo[anoLetivoSelecionado][unidadeKey];
      const dataFormatada = brToApiDate(dataInicioBr);

      // Adicionar timeout de 2 minutos para evitar travamento
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Timeout: A requisição demorou mais de 2 minutos. A API pode estar sobrecarregada.')), 120000);
      });

      const matriculasData = await Promise.race([
        getAllMatriculas(dataFormatada, situacaoID, anoLetivoSelecionado),
        timeoutPromise
      ]);
      if (request !== enrollmentRequestRef.current || scope !== reportScopeRef.current) return;
      const matriculasPayload = Array.isArray(matriculasData)
        ? { matriculas: matriculasData, enrollmentContractStatuses: [] }
        : { matriculas: matriculasData?.matriculas || [], enrollmentContractStatuses: matriculasData?.enrollmentContractStatuses || [] };
      // Armazenar os dados garantindo array e filtrando objetos vazios/inválidos
      const asArray = Array.isArray(matriculasPayload.matriculas)
        ? matriculasPayload.matriculas
        : matriculasPayload.matriculas
        ? [matriculasPayload.matriculas]
        : [];
      
      // Filtrar objetos vazios ou sem dados úteis
      const validMatriculas = readEnrollmentResponse(asArray).filter(isValidMatricula);
      const localStatuses = statusesByStudentId(matriculasPayload.enrollmentContractStatuses || []);
      // O endpoint de matrículas já devolve os status em uma única consulta local.
      setPaymentReportError(matriculasData?.enrollmentContractStatusStorage?.available === false
        ? "Os dados locais de contrato, pagamento e imagem estão indisponíveis. As matrículas foram mantidas. Atualize o relatório ou solicite suporte."
        : "");
      setPaymentReportUpdatedAt(matriculasData?.enrollmentContractStatusStorage?.available === false ? null : new Date());
      setContractStatusesByStudentId(localStatuses);
      
      setMatriculas(validMatriculas);
      
      // Marcar que as matrículas foram buscadas
      setDadosBuscados(prev => ({ ...prev, matriculas: true }));
    } catch (error) {
      if (request !== enrollmentRequestRef.current || scope !== reportScopeRef.current) return;
      console.error("[CensoEscolar] Erro na busca:", error);
      setPaymentReportError("Não foi possível atualizar as matrículas. A consulta anterior foi preservada. Tente novamente.");
      // Mostrar mensagem de erro mais amigável
      if (error.message?.includes('Timeout')) {
        alert('A requisição está demorando muito. Isso pode acontecer quando há muitas requisições na fila da API Sponte. Tente novamente em alguns instantes.');
      }
    } finally {
      clearTimeout(timeoutId);
      if (request === enrollmentRequestRef.current && scope === reportScopeRef.current) setLoadingStates(prev => ({ ...prev, matriculas: false }));
    }
  };

  // Função para atualizar status de pré-matrículas para vigente
  const handleAtualizarPreMatriculasParaVigente = async () => {
    // Filtrar apenas pré-matrículas (situação 6)
    const preMatriculas = matriculas.filter((m) => {
      const situacao = m?.SituacaoID || m?.Situacao || m?.SituacaoContrato;
      return String(situacao) === '6';
    });

    if (preMatriculas.length === 0) {
      alert('Não há pré-matrículas para atualizar.');
      return;
    }

    // Confirmar ação
    const confirmacao = window.confirm(
      `Você está prestes a atualizar ${preMatriculas.length} pré-matrícula(s) para status VIGENTE.\n\n` +
      `Esta ação não pode ser desfeita. Deseja continuar?`
    );

    if (!confirmacao) {
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, atualizarStatus: true }));
      
      // Extrair IDs dos contratos
      const contratoIDs = preMatriculas
        .map((m) => m?.ContratoID || m?.ContratoId || m?.Contrato || m?.ContratoAulaLivreID)
        .filter(Boolean);

      if (contratoIDs.length === 0) {
        alert('Não foi possível identificar os IDs dos contratos.');
        return;
      }

      // Processar UM contrato por vez com delay entre cada requisição
      // Esta é a estratégia mais segura para evitar bloqueio da API Sponte
      // Limite da Sponte: 200 req/min, mas parece que após ~100 já começa a falhar
      // Processando 1 por vez com delay de 3 segundos = 20 req/min (extremamente seguro)
      const delayEntreRequisicoes = 3000; // 3 segundos entre cada requisição
      let totalProcessados = 0;
      let totalSucesso = 0;
      let totalErros = 0;

      setProgressStates(prev => ({
        ...prev,
        atualizarStatus: { current: 0, total: contratoIDs.length }
      }));

      // Processar cada contrato individualmente com delay
      for (let i = 0; i < contratoIDs.length; i++) {
        const contratoID = contratoIDs[i];
        
        // Adicionar delay entre requisições (exceto na primeira)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, delayEntreRequisicoes));
        }
        
        // Capturar valores atuais para evitar referências inseguras em loops
        const currentProcessados = totalProcessados;
        const currentSucesso = totalSucesso;
        const currentErros = totalErros;
        
        try {
          // Processar um contrato por vez usando o endpoint batch (que aceita array de 1)
          const resultado = await setAllContractsBatch([contratoID], 1); // 1 = Vigente
          
          // Verificar se resultado é válido
          if (!resultado || !resultado.data) {
            throw new Error('Resposta inválida do servidor');
          }
          
          // Verificar se foi sucesso ou erro
          const resultadoContrato = resultado.data[contratoID];
          if (resultadoContrato && resultadoContrato.success === true) {
            totalSucesso++;
          } else {
            totalErros++;
            
            // Se for erro de rate limit, parar processamento
            const errorMsg = resultadoContrato?.error || '';
            if (errorMsg.includes('429') || errorMsg.includes('rate limit') || errorMsg.includes('bloqueada')) {
              const waitTime = 120; // 2 minutos (tempo de bloqueio da Sponte)
              alert(
                `⚠️ Limite de requisições atingido!\n\n` +
                `A API Sponte foi bloqueada temporariamente.\n` +
                `Aguarde ${waitTime} segundos (2 minutos) antes de tentar novamente.\n\n` +
                `Processados até agora: ${currentProcessados + 1} de ${contratoIDs.length}\n` +
                `Sucessos: ${currentSucesso}\n` +
                `Erros: ${currentErros + 1}`
              );
              break;
            }
          }

          totalProcessados++;

          // Atualizar progresso usando função de atualização para evitar referências inseguras
          setProgressStates(prev => ({
            ...prev,
            atualizarStatus: { current: prev.atualizarStatus.current + 1, total: contratoIDs.length }
          }));
        } catch (error) {
          totalErros++;
          totalProcessados++;
          
          // Se for erro de conexão, mostrar mensagem específica
          if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_CONNECTION_REFUSED')) {
            alert('❌ Erro de conexão: O servidor backend não está respondendo.\n\nVerifique se o backend está rodando na porta 3001 (Campina) ou 3002 (Patos).');
            break;
          }
          
          // Se for erro de rate limit, mostrar alerta e parar
          if (error.response?.status === 429 || error.rateLimitExceeded || error.response?.data?.rateLimitExceeded) {
            const waitTime = error.response?.data?.waitTime || error.waitTime || 120; // 2 minutos padrão
            const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Limite de requisições atingido';
            alert(
              `⚠️ ${message}\n\n` +
              `A API Sponte foi bloqueada temporariamente.\n` +
              `Aguarde ${waitTime} segundos (${Math.round(waitTime / 60)} minutos) antes de tentar novamente.\n\n` +
              `Processados até agora: ${currentProcessados + 1} de ${contratoIDs.length}\n` +
              `Sucessos: ${currentSucesso}\n` +
              `Erros: ${currentErros + 1}`
            );
            break;
          }
          
          // Atualizar progresso mesmo em caso de erro usando função de atualização
          setProgressStates(prev => ({
            ...prev,
            atualizarStatus: { current: prev.atualizarStatus.current + 1, total: contratoIDs.length }
          }));
        }
      }

      // Mostrar resultado final
      if (totalSucesso > 0) {
        alert(
          `✅ Atualização concluída!\n\n` +
          `Total processado: ${totalProcessados}\n` +
          `Sucessos: ${totalSucesso}\n` +
          `Erros: ${totalErros}\n\n` +
          `Recarregando lista de matrículas...`
        );

        // Recarregar matrículas para refletir as mudanças
        await handleGetMatriculas();
      } else {
        alert(`❌ Nenhuma atualização foi realizada.\n\nErros: ${totalErros}`);
      }
    } catch (error) {
      
      // Mensagem de erro mais específica
      let errorMessage = 'Erro ao atualizar status das pré-matrículas.';
      
      if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = '❌ Erro de conexão: O servidor backend não está respondendo.\n\nVerifique se o backend está rodando.';
      } else if (error.response?.status === 429 || error.rateLimitExceeded) {
        const waitTime = error.response?.data?.waitTime || error.waitTime || 60;
        errorMessage = `⚠️ Limite de requisições atingido.\n\nAguarde ${waitTime} segundos antes de tentar novamente.`;
      } else if (error.response?.data?.error) {
        errorMessage = `Erro: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoadingStates(prev => ({ ...prev, atualizarStatus: false }));
      setProgressStates(prev => ({
        ...prev,
        atualizarStatus: { current: 0, total: 0 }
      }));
    }
  };

  // Senha obrigatória para executar "Encerrar contratos vigentes"
  const SENHA_ENCERRAR_CONTRATOS = '@Ag72qjh5';

  // Função para atualizar status de contratos vigentes para encerrado (apenas administrador)
  const handleAtualizarVigentesParaEncerrado = async (skipConfirm = false) => {
    // Filtrar apenas vigentes (situação 1)
    const vigentes = matriculas.filter((m) => {
      const situacao = m?.SituacaoID || m?.Situacao || m?.SituacaoContrato;
      return String(situacao) === '1';
    });

    if (vigentes.length === 0) {
      alert('Não há contratos vigentes para encerrar.');
      return;
    }

    if (!skipConfirm) {
      const confirmacao = window.confirm(
        `Você está prestes a encerrar ${vigentes.length} contrato(s) vigente(s).\n\n` +
        `O status será alterado para ENCERRADO. Esta ação não pode ser desfeita. Deseja continuar?`
      );
      if (!confirmacao) return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, atualizarStatus: true }));

      const contratoIDs = vigentes
        .map((m) => m?.ContratoID || m?.ContratoId || m?.Contrato || m?.ContratoAulaLivreID)
        .filter(Boolean);

      if (contratoIDs.length === 0) {
        alert('Não foi possível identificar os IDs dos contratos.');
        return;
      }

      const delayEntreRequisicoes = 3000;
      let totalProcessados = 0;
      let totalSucesso = 0;
      let totalErros = 0;

      setProgressStates(prev => ({
        ...prev,
        atualizarStatus: { current: 0, total: contratoIDs.length }
      }));

      for (let i = 0; i < contratoIDs.length; i++) {
        const contratoID = contratoIDs[i];

        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, delayEntreRequisicoes));
        }

        const currentProcessados = totalProcessados;
        const currentSucesso = totalSucesso;
        const currentErros = totalErros;

        try {
          // 2 = Encerrado (Sponte)
          const resultado = await setAllContractsBatch([contratoID], 2);

          if (!resultado || !resultado.data) {
            throw new Error('Resposta inválida do servidor');
          }

          const resultadoContrato = resultado.data[contratoID];
          if (resultadoContrato && resultadoContrato.success === true) {
            totalSucesso++;
          } else {
            totalErros++;
            const errorMsg = resultadoContrato?.error || '';
            if (errorMsg.includes('429') || errorMsg.includes('rate limit') || errorMsg.includes('bloqueada')) {
              const waitTime = 120;
              alert(
                `⚠️ Limite de requisições atingido!\n\n` +
                `A API Sponte foi bloqueada temporariamente.\n` +
                `Aguarde ${waitTime} segundos (2 minutos) antes de tentar novamente.\n\n` +
                `Processados até agora: ${currentProcessados + 1} de ${contratoIDs.length}\n` +
                `Sucessos: ${currentSucesso}\n` +
                `Erros: ${currentErros + 1}`
              );
              break;
            }
          }

          totalProcessados++;
          setProgressStates(prev => ({
            ...prev,
            atualizarStatus: { current: prev.atualizarStatus.current + 1, total: contratoIDs.length }
          }));
        } catch (error) {
          totalErros++;
          totalProcessados++;

          if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_CONNECTION_REFUSED')) {
            alert('❌ Erro de conexão: O servidor backend não está respondendo.\n\nVerifique se o backend está rodando na porta 3001 (Campina) ou 3002 (Patos).');
            break;
          }

          if (error.response?.status === 429 || error.rateLimitExceeded || error.response?.data?.rateLimitExceeded) {
            const waitTime = error.response?.data?.waitTime || error.waitTime || 120;
            const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Limite de requisições atingido';
            alert(
              `⚠️ ${message}\n\n` +
              `A API Sponte foi bloqueada temporariamente.\n` +
              `Aguarde ${waitTime} segundos (${Math.round(waitTime / 60)} minutos) antes de tentar novamente.\n\n` +
              `Processados até agora: ${totalProcessados} de ${contratoIDs.length}\n` +
              `Sucessos: ${totalSucesso}\n` +
              `Erros: ${totalErros}`
            );
            break;
          }

          setProgressStates(prev => ({
            ...prev,
            atualizarStatus: { current: prev.atualizarStatus.current + 1, total: contratoIDs.length }
          }));
        }
      }

      if (totalSucesso > 0) {
        alert(
          `✅ Encerramento concluído!\n\n` +
          `Total processado: ${totalProcessados}\n` +
          `Sucessos: ${totalSucesso}\n` +
          `Erros: ${totalErros}\n\n` +
          `Recarregando lista de matrículas...`
        );
        await handleGetMatriculas();
      } else {
        alert(`❌ Nenhum contrato foi encerrado.\n\nErros: ${totalErros}`);
      }
    } catch (error) {
      let errorMessage = 'Erro ao encerrar contratos vigentes.';
      if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = '❌ Erro de conexão: O servidor backend não está respondendo.\n\nVerifique se o backend está rodando.';
      } else if (error.response?.status === 429 || error.rateLimitExceeded) {
        const waitTime = error.response?.data?.waitTime || error.waitTime || 60;
        errorMessage = `⚠️ Limite de requisições atingido.\n\nAguarde ${waitTime} segundos antes de tentar novamente.`;
      } else if (error.response?.data?.error) {
        errorMessage = `Erro: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      alert(errorMessage);
    } finally {
      setLoadingStates(prev => ({ ...prev, atualizarStatus: false }));
      setProgressStates(prev => ({
        ...prev,
        atualizarStatus: { current: 0, total: 0 }
      }));
    }
  };

  return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      {paymentReportError && <Alert severity="error" sx={{ mb: 2 }}>{paymentReportError}</Alert>}
      <Typography variant="h5" gutterBottom>
        Relatório de Matrículas
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Selecione o ano letivo e a situação para buscar dados das matrículas.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, maxWidth: 700, mx: "auto" }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ano-letivo-label">Ano Letivo</InputLabel>
              <Select
                labelId="ano-letivo-label"
                value={anoLetivoSelecionado}
                label="Ano Letivo"
                onChange={(e) => changeEnrollmentContext(setAnoLetivoSelecionado, e.target.value)}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="situacao-label">Situação</InputLabel>
              <Select
                labelId="situacao-label"
                value={situacaoID}
                label="Situação"
                onChange={(e) => changeEnrollmentContext(setSituacaoID, e.target.value)}
              >
                {situacoes.map((situacao) => (
                  <MenuItem key={situacao.id} value={situacao.id}>
                    {situacao.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

           {/* Botão principal - Buscar Matrículas */}
           <Grid item xs={12}>
             <Button
               variant="contained"
               color="primary"
               onClick={handleGetMatriculas}
               disabled={loadingStates.matriculas}
               size="large"
               sx={{ 
                 minWidth: 300, 
                 mt: 2, 
                 py: 1.5,
                 fontSize: '1.1rem',
                 fontWeight: 'bold'
               }}
               fullWidth
             >
               {loadingStates.matriculas ? (
                 <>
                   <CircularProgress size={24} sx={{ mr: 2 }} />
                   Carregando Matrículas...
                 </>
               ) : (
                 "Buscar Matrículas"
               )}
             </Button>
           </Grid>

           {/* Botão para atualizar pré-matrículas para vigente - aparece apenas para admins/coordinators e quando há pré-matrículas */}
           {isAdminOrCoordinator() && matriculas.length > 0 && (() => {
             const preMatriculas = matriculas.filter((m) => {
               const situacao = m?.SituacaoID || m?.Situacao || m?.SituacaoContrato;
               return String(situacao) === '6';
             });
             
             if (preMatriculas.length === 0) return null;
             
             return (
               <Grid item xs={12}>
                 <Button
                   variant="contained"
                   color="success"
                   onClick={handleAtualizarPreMatriculasParaVigente}
                   disabled={loadingStates.atualizarStatus || loadingStates.matriculas}
                   size="large"
                   sx={{ 
                     minWidth: 300, 
                     mt: 2, 
                     py: 1.5,
                     fontSize: '1.1rem',
                     fontWeight: 'bold',
                     backgroundColor: '#2e7d32',
                     '&:hover': {
                       backgroundColor: '#1b5e20',
                     }
                   }}
                   fullWidth
                 >
                   {loadingStates.atualizarStatus ? (
                     <>
                       <CircularProgress size={24} sx={{ mr: 2 }} color="inherit" />
                       {progressStates.atualizarStatus.total > 0 ? 
                         `Atualizando... ${getProgressPercentage(progressStates.atualizarStatus.current, progressStates.atualizarStatus.total)}% (${progressStates.atualizarStatus.current}/${progressStates.atualizarStatus.total})` : 
                         'Atualizando...'}
                     </>
                   ) : (
                     `Atualizar ${preMatriculas.length} Pré-Matrícula(s) para Vigente`
                   )}
                 </Button>
               </Grid>
             );
           })()}

           {/* Botão para encerrar contratos vigentes - apenas administrador, quando há vigentes */}
           {isAdmin() && matriculas.length > 0 && (() => {
             const vigentes = matriculas.filter((m) => {
               const situacao = m?.SituacaoID || m?.Situacao || m?.SituacaoContrato;
               return String(situacao) === '1';
             });

             if (vigentes.length === 0) return null;

             return (
               <Grid item xs={12}>
                 <Button
                   variant="contained"
                   color="warning"
                   onClick={() => {
                     setEncerrarPasswordError('');
                     setEncerrarPasswordInput('');
                     setEncerrarPasswordDialogOpen(true);
                   }}
                   disabled={loadingStates.atualizarStatus || loadingStates.matriculas}
                   size="large"
                   sx={{
                     minWidth: 300,
                     mt: 2,
                     py: 1.5,
                     fontSize: '1.1rem',
                     fontWeight: 'bold',
                     backgroundColor: '#ed6c02',
                     '&:hover': {
                       backgroundColor: '#e65100',
                     }
                   }}
                   fullWidth
                 >
                   {loadingStates.atualizarStatus ? (
                     <>
                       <CircularProgress size={24} sx={{ mr: 2 }} color="inherit" />
                       {progressStates.atualizarStatus.total > 0
                         ? `Encerrando... ${getProgressPercentage(progressStates.atualizarStatus.current, progressStates.atualizarStatus.total)}% (${progressStates.atualizarStatus.current}/${progressStates.atualizarStatus.total})`
                         : 'Encerrando...'}
                     </>
                   ) : (
                     `Encerrar ${vigentes.length} Contrato(s) Vigente(s)`
                   )}
                 </Button>
               </Grid>
             );
           })()}

           {/* Botões secundários - aparecem apenas após buscar matrículas e quando há matrículas válidas */}
           {dadosBuscados.matriculas && matriculas.length > 0 && (
             <>
               <Grid item xs={12}>
                 <Typography variant="h6" sx={{ mt: 3, mb: 2, textAlign: 'left', color: 'text.secondary' }}>
                   Dados Complementares
                 </Typography>
               </Grid>
               
               <Grid item xs={12} md={4}>
                 <Button
                   variant="outlined"
                   color="secondary"
                   onClick={handleBuscarAlunos}
                   disabled={loadingStates.alunos || dadosBuscados.alunos}
                   sx={{ 
                     minWidth: 200, 
                     mt: 1,
                     py: 1,
                     borderWidth: 2,
                     '&:hover': {
                       borderWidth: 2
                     }
                   }}
                   fullWidth
                 >
                   {loadingStates.alunos ? (
                     <>
                       <CircularProgress size={20} sx={{ mr: 1 }} />
                       {progressStates.alunos.total > 0 ? 
                         `${getProgressPercentage(progressStates.alunos.current, progressStates.alunos.total)}%` : 
                         'Carregando...'
                       }
                     </>
                   ) : dadosBuscados.alunos ? (
                     "✓ Dados do Aluno"
                   ) : (
                     "Buscar Dados do Aluno"
                   )}
                 </Button>
               </Grid>
               
               <Grid item xs={12} md={4}>
                 <Button
                   variant="outlined"
                   color="success"
                   onClick={handleBuscarResponsaveis}
                   disabled={loadingStates.responsaveis || dadosBuscados.responsaveis}
                   sx={{ 
                     minWidth: 200, 
                     mt: 1,
                     py: 1,
                     borderWidth: 2,
                     '&:hover': {
                       borderWidth: 2
                     }
                   }}
                   fullWidth
                 >
                   {loadingStates.responsaveis ? (
                     <>
                       <CircularProgress size={20} sx={{ mr: 1 }} />
                       {progressStates.responsaveis.total > 0 ? 
                         `${getProgressPercentage(progressStates.responsaveis.current, progressStates.responsaveis.total)}%` : 
                         'Carregando...'
                       }
                     </>
                   ) : dadosBuscados.responsaveis ? (
                     "✓ Dados do Responsável"
                   ) : (
                     "Buscar Dados do Responsável"
                   )}
                 </Button>
               </Grid>
               
               <Grid item xs={12} md={4}>
                 <Button
                   variant="outlined"
                   color="warning"
                   onClick={handleBuscarFinanceiro}
                   disabled={loadingStates.financeiro || dadosBuscados.financeiro}
                   sx={{ 
                     minWidth: 200, 
                     mt: 1,
                     py: 1,
                     borderWidth: 2,
                     '&:hover': {
                       borderWidth: 2
                     }
                   }}
                   fullWidth
                 >
                   {loadingStates.financeiro ? (
                     <>
                       <CircularProgress size={20} sx={{ mr: 1 }} />
                       {progressStates.financeiro.total > 0 ? 
                         `${getProgressPercentage(progressStates.financeiro.current, progressStates.financeiro.total)}%` : 
                         'Carregando...'
                       }
                     </>
                   ) : dadosBuscados.financeiro ? (
                     "✓ Dados Financeiros"
                   ) : (
                     "Buscar Dados Financeiros"
                   )}
                 </Button>
               </Grid>
             </>
           )}

           {/* Histórico também disponível quando a consulta atual confirmou zero matrículas. */}
           {(matriculas.length > 0 || dadosBuscados.matriculas) && (
             <Grid item xs={12}>
               <Button
                 variant="contained"
                 color="info"
                 onClick={handleShowAnalise}
                 sx={{ 
                   minWidth: 300, 
                   mt: 3, 
                   py: 1.5,
                   fontSize: '1.1rem',
                   fontWeight: 'bold'
                 }}
                 fullWidth
               >
                 Ver Análise Estatística
               </Button>
             </Grid>
           )}
        </Grid>
      </Paper>

      {/* Filtros de resultado */}
      {matriculas.length > 0 && (
        <Paper elevation={2} sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: "left" }}>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Início (Data da Matrícula)"
                type="date"
                value={filterStartDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Fim (Data da Matrícula)"
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                inputProps={{ min: filterStartDate || undefined }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="curso-filter-label">Curso</InputLabel>
                <Select
                  labelId="curso-filter-label"
                  value={filterCurso}
                  label="Curso"
                  onChange={(e) => setFilterCurso(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {[
                    ...new Set(
                      (matriculas || [])
                        .map((m) => m.NomeCurso || m.CursoNome)
                        .filter(Boolean)
                    ),
                  ]
                    .sort()
                    .map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="tipo-filter-label">
                  Tipo da Matrícula
                </InputLabel>
                <Select
                  labelId="tipo-filter-label"
                  value={filterTipo}
                  label="Tipo da Matrícula"
                  onChange={(e) => setFilterTipo(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="-1">Matrícula</MenuItem>
                  <MenuItem value="-2">Rematrícula</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="turno-filter-label">Turno</InputLabel>
                <Select
                  labelId="turno-filter-label"
                  value={filterTurno}
                  label="Turno"
                  onChange={(e) => setFilterTurno(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="MANHÃ">MANHÃ</MenuItem>
                  <MenuItem value="TARDE">TARDE</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterExcluirIntegral}
                    onChange={(e) => setFilterExcluirIntegral(e.target.checked)}
                    color="primary"
                  />
                }
                label="Excluir Integral"
                sx={{ 
                  height: "100%", 
                  display: 'flex', 
                  alignItems: 'center',
                  margin: 0
                }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                fullWidth
                sx={{ height: "100%" }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Exibição dos resultados */}
      {matriculas.length > 0 && sortedMatriculas.length > 0 && (
        <Paper elevation={2} sx={{ mt: 3, p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ textAlign: "left" }}>
              Resultados da Busca ({filteredMatriculas.length} de{" "}
              {matriculas.length} matrículas)
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", ml: 2 }}>
	              <EnrollmentPaymentReport key={reportScope} error={paymentReportError} updatedAt={paymentReportUpdatedAt} onLoadLegacyStudents={() => handleBuscarAlunos(true, sortedMatriculas.filter(isValidMatricula).map(getMatriculaAlunoId).filter(id => !contractStatusesByStudentId[id]))} enrollments={sortedMatriculas.filter(isValidMatricula)} students={alunosById} contractStatuses={contractStatusesByStudentId} onLoadStudents={handleRefreshEnrollmentStatuses} onSaveStatus={handleSaveEnrollmentStatus} loading={paymentReportLoading || loadingStates.alunos} />
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={printGroupedByClassroom}
                disabled={!groupedByClassroom.length}
                sx={{ minWidth: 190 }}
              >
                Imprimir por Turma
              </Button>
	              <Button
	                variant="contained"
	                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportToExcel}
                sx={{
                  minWidth: 180,
                }}
              >
                Exportar Excel
              </Button>
	            </Box>
	          </Box>

	          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sortDirection={sortBy === "data" ? sortDir : false}
                  >
                    <TableSortLabel
                      active={sortBy === "data"}
                      direction={sortBy === "data" ? sortDir : "asc"}
                      onClick={() => toggleSort("data")}
                    >
                      Data da Matrícula
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Curso</TableCell>
                  <TableCell>Tipo da Matrícula</TableCell>
                  <TableCell>Turno</TableCell>
                  
                  {/* Colunas condicionais baseadas nos dados buscados */}
                  {dadosBuscados.alunos && (
                    <>
                      <TableCell
                        sortDirection={sortBy === "aluno" ? sortDir : false}
                      >
                        <TableSortLabel
                          active={sortBy === "aluno"}
                          direction={sortBy === "aluno" ? sortDir : "asc"}
                          onClick={() => toggleSort("aluno")}
                        >
                          Nome do Aluno
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Data de Nascimento</TableCell>
                      <TableCell>CPF</TableCell>
                      <TableCell>CEP</TableCell>
                      <TableCell>Nº Endereço</TableCell>
                      <TableCell>Nº Matrícula</TableCell>
                    </>
                  )}
                  
                  {dadosBuscados.responsaveis && (
                    <>
                      <TableCell>Resp. Financeiro</TableCell>
                      <TableCell>CPF Resp.</TableCell>
                    </>
                  )}
                  
                  {dadosBuscados.financeiro && (
                    <>
                      <TableCell
                        sortDirection={sortBy === "valor" ? sortDir : false}
                      >
                        <TableSortLabel
                          active={sortBy === "valor"}
                          direction={sortBy === "valor" ? sortDir : "asc"}
                          onClick={() => toggleSort("valor")}
                        >
                          Valor da Parcela
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sortDirection={sortBy === "desconto" ? sortDir : false}
                      >
                        <TableSortLabel
                          active={sortBy === "desconto"}
                          direction={sortBy === "desconto" ? sortDir : "asc"}
                          onClick={() => toggleSort("desconto")}
                        >
                          Desconto
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                  
                  {/* Coluna de Pendências - sempre visível */}
                  <TableCell
                    align="center"
                    sortDirection={sortBy === "pendencias" ? sortDir : false}
                    sx={{
                      minWidth: 120,
                      width: 120,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "pendencias"}
                      direction={sortBy === "pendencias" ? sortDir : "asc"}
                      onClick={() => toggleSort("pendencias")}
                    >
                      Pendências
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Filtrar apenas matrículas válidas
                  const validMatriculas = sortedMatriculas.filter(isValidMatricula);
                  
                  if (validMatriculas.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={100} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhuma matrícula encontrada com os critérios selecionados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  return validMatriculas.map((matricula, index) => {
                  const alunoId = getMatriculaAlunoId(matricula);
                  const aluno = alunosById[String(alunoId)] || {};
                  const resp = responsavelByAlunoId[String(alunoId)] || {};
                  const contratoId =
                    matricula?.ContratoID ||
                    matricula?.ContratoId ||
                    matricula?.Contrato ||
                    matricula?.ContratoAulaLivreID;
                  const finDerivado =
                    financeiroDerivadoByContratoId[String(contratoId)] || {};
                  
                  const isPending = pendingIds.has(String(alunoId));
                  const isPendingResp = pendingRespIds.has(String(alunoId));
                  const isPendingFin = pendingFinIds.has(String(contratoId));
                  
                  const renderCell = (value) =>
                    isPending && !value ? (
                      <Skeleton variant="text" sx={{ minWidth: 80 }} />
                    ) : (
                      value || "N/A"
                    );
                  const renderRespCell = (value) =>
                    isPendingResp && !value ? (
                      <Skeleton variant="text" sx={{ minWidth: 120 }} />
                    ) : (
                      value || "N/A"
                    );
                  const renderFinCell = (value, isPercentage = false) => {
                    if (isPendingFin && !value) {
                      return <Skeleton variant="text" sx={{ minWidth: 100 }} />;
                    }
                    if (value === null || value === undefined || value === "" || isNaN(value)) {
                      return "N/A";
                    }
                    if (typeof value === 'number' && !isNaN(value)) {
                      if (isPercentage) {
                        return `${value}%`;
                      }
                      return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
                    }
                    return value || "N/A";
                  };

                  return (
                    <TableRow key={matricula.ContratoID || index}>
                      <TableCell>{matricula.DataMatricula || "N/A"}</TableCell>
                      <TableCell>
                        {matricula.NomeCurso || matricula.CursoNome || "N/A"}
                      </TableCell>
                      <TableCell>
                        {mapTipoMatricula(matricula.TipoContratoID)}
                      </TableCell>
                      <TableCell>
                        {extractTurno(matricula.NomeTurma || matricula.TurmaNome)}
                      </TableCell>
                      
                      {/* Colunas condicionais baseadas nos dados buscados */}
                      {dadosBuscados.alunos && (
                        <>
                          <TableCell>
                            {matricula.Aluno || matricula.AlunoNome || "N/A"}
                          </TableCell>
                          <TableCell>
                            {renderCell(
                              aluno.DataNascimento ||
                              aluno.DataNasc ||
                              aluno.Nascimento ||
                              aluno.DtNascimento ||
                              matricula.DataNascimento
                            )}
                          </TableCell>
                          <TableCell>
                            {renderCell(
                              aluno.CPF ||
                              aluno.Cpf ||
                              aluno.CPFAluno ||
                              matricula.CPF ||
                              matricula.Cpf
                            )}
                          </TableCell>
                          <TableCell>
                            {renderCell(aluno.CEP || aluno.Cep)}
                          </TableCell>
                          <TableCell>
                            {renderCell(aluno.NumeroEndereco || aluno.Numero)}
                          </TableCell>
                          <TableCell>
                            {renderCell(
                              aluno.NumeroMatricula ||
                              matricula.NumeroMatricula
                            )}
                          </TableCell>
                        </>
                      )}
                      
                      {dadosBuscados.responsaveis && (
                        <>
                          <TableCell>
                            {renderRespCell(
                              resp.Nome || resp.nome || resp.ResponsavelNome
                            )}
                          </TableCell>
                          <TableCell>
                            {renderRespCell(resp.CPFCNPJ || resp.CPF || resp.Cpf)}
                          </TableCell>
                        </>
                      )}
                      
                      {dadosBuscados.financeiro && (
                        <>
                          <TableCell>
                            {renderFinCell(finDerivado.valorParcela5, false)}
                          </TableCell>
                          <TableCell>
                            {renderFinCell(finDerivado.descontoPercent, true)}
                          </TableCell>
                        </>
                      )}
                      
                      {/* Célula de Pendências - sempre visível */}
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 120,
                          width: 120,
                        }}
                      >
                        {(() => {
                          const pendencias = verificarPendencias(matricula, aluno, resp);
                          const temPendencias = pendencias.length > 0;
                          
                          return (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPendencies(matricula, aluno, resp)}
                              color={temPendencias ? "warning" : "success"}
                            >
                              {temPendencias ? (
                                <WarningIcon />
                              ) : (
                                <CheckCircleIcon />
                              )}
                            </IconButton>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

       {(matriculas.length === 0 || (matriculas.length > 0 && sortedMatriculas.length === 0)) && !loading && (
         <Alert severity="info" sx={{ mt: 3, textAlign: "center" }}>
           {matriculas.length === 0 
             ? "Nenhuma matrícula encontrada. Clique em 'Buscar Matrículas' para iniciar a busca."
             : "Nenhuma matrícula encontrada com os critérios selecionados."}
         </Alert>
       )}

       {/* Diálogo de senha para encerrar contratos vigentes */}
       <Dialog
         open={encerrarPasswordDialogOpen}
         onClose={() => {
           setEncerrarPasswordDialogOpen(false);
           setEncerrarPasswordInput('');
           setEncerrarPasswordError('');
         }}
         maxWidth="xs"
         fullWidth
       >
         <DialogTitle>Confirmar encerramento de contratos</DialogTitle>
         <DialogContent>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             Digite a senha para autorizar o encerramento dos contratos vigentes.
           </Typography>
           <TextField
             autoFocus
             fullWidth
             type="password"
             label="Senha"
             value={encerrarPasswordInput}
             onChange={(e) => {
               setEncerrarPasswordInput(e.target.value);
               setEncerrarPasswordError('');
             }}
             error={Boolean(encerrarPasswordError)}
             helperText={encerrarPasswordError}
             onKeyDown={(e) => {
               if (e.key === 'Enter') {
                 e.preventDefault();
                 if (encerrarPasswordInput.trim() === SENHA_ENCERRAR_CONTRATOS) {
                   setEncerrarPasswordDialogOpen(false);
                   setEncerrarPasswordInput('');
                   setEncerrarPasswordError('');
                   handleAtualizarVigentesParaEncerrado(true);
                 } else {
                   setEncerrarPasswordError('Senha incorreta.');
                 }
               }
             }}
           />
         </DialogContent>
         <DialogActions>
           <Button
             onClick={() => {
               setEncerrarPasswordDialogOpen(false);
               setEncerrarPasswordInput('');
               setEncerrarPasswordError('');
             }}
           >
             Cancelar
           </Button>
           <Button
             variant="contained"
             color="warning"
             onClick={() => {
               if (encerrarPasswordInput.trim() === SENHA_ENCERRAR_CONTRATOS) {
                 setEncerrarPasswordDialogOpen(false);
                 setEncerrarPasswordInput('');
                 setEncerrarPasswordError('');
                 handleAtualizarVigentesParaEncerrado(true);
               } else {
                 setEncerrarPasswordError('Senha incorreta.');
               }
             }}
           >
             Confirmar
           </Button>
         </DialogActions>
       </Dialog>

       {/* Modal de Pendências */}
       <Dialog 
         open={pendenciesModalOpen} 
         onClose={() => setPendenciesModalOpen(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           Pendências - {selectedStudentName}
         </DialogTitle>
         <DialogContent>
           {selectedStudentPendencies.length === 0 ? (
             <Box sx={{ textAlign: 'center', py: 2 }}>
               <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
               <Typography variant="h6" color="success.main">
                 Nenhuma pendência encontrada!
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Todos os campos obrigatórios estão preenchidos.
               </Typography>
             </Box>
           ) : (
             <Box>
               <Typography variant="body1" gutterBottom>
                 Os seguintes campos precisam ser preenchidos:
               </Typography>
               <List>
                 {selectedStudentPendencies.map((pendencia, index) => (
                   <ListItem key={index}>
                     <ListItemIcon>
                       <WarningIcon color="warning" />
                     </ListItemIcon>
                     <ListItemText primary={pendencia} />
                   </ListItem>
                 ))}
               </List>
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setPendenciesModalOpen(false)}>
             Fechar
           </Button>
         </DialogActions>
       </Dialog>

     </Box>
   );
 };
